import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Monitor, Maximize2, Minimize2, Plus, Pencil, Star, StarOff,
  LayoutGrid, Save, X, Trash2, Activity, ChevronDown,
} from 'lucide-react';
import {
  ToolbarIdentity,
  Tooltip,
  usePeerLatency,
  LiveWallCameraPicker,
  useWallMerge,
  WallGridResizer, useWallTrackSizes, trackTemplate,
  WallSelectionOverlay,
  WallMergeBar,
  WallSplitButton,
  WallEmptyWindow,
  WallEditGuide,
  WallGridMenu,
  normalizeWallMerges,
  flowWallCameras,
  wallGridArea,
  WallModeToggle,
  liveWallHandOver,
  wallPlaybackHref,
  type WallMerge,
  type WallPreset,
} from '@trinetra/shared';
import StreamTile from '../components/multi-site/StreamTile';
import CameraFormModal from '../components/modals/CameraFormModal';
import { useAuth } from '../contexts/AuthContext';
import { useNode } from '../contexts/NodeContext';
import { useNodeStream } from '../contexts/NodeStreamContext';
import {
  getNodeCameras,
  getNodeGroups,
  getNodeGroupsForFilter,
  getNodeLiveWallViews,
  getNodeLiveWallDefault,
  createNodeLiveWallView,
  updateNodeLiveWallView,
  deleteNodeLiveWallView,
  setNodeLiveWallDefault,
  clearNodeLiveWallDefault,
  type SiteLiveWallView,
} from '../services/api.service';

/**
 * Live Wall for ONE site — a saved grid of the selected node's cameras.
 *
 * Two Live Walls exist in this app and they are not the same page:
 *   • MultiSiteView (`/all-sites/live-wall`, ALL SITES scope) — a grid that can mix cameras from
 *     any node, built from layouts stored in the CLOUD database.
 *   • this one (`/live-wall`, ONE SITE scope) — a grid of one node's cameras, built from
 *     layouts stored on THAT NODE.
 *
 * The distinction is deliberate: a per-site wall reads and writes the very layouts the node's
 * own local dashboard shows, so an operator who arranges a wall in the control room sees the
 * same wall here, and neither copy can drift. That is also why this page is much simpler than
 * MultiSiteView — one site means one tunnel, one peer connection and one RTT, so none of the
 * per-node reconciliation that page needs applies here.
 *
 * Streaming is the app-level NodeStreamProvider, same as everywhere else: this page subscribes
 * and unsubscribes, it does not own a connection.
 */

// Keep in step with LIVE_WALL_GRIDS in the local backend's config/constants.js — the node's
// validator only accepts sizes from that list.
type GridSize = '2x2' | '2x3' | '3x3' | '3x4' | '4x4' | '4x6' | '4x8' | '6x8' | '8x8';

const GRID_SIZES: GridSize[] = ['2x2', '2x3', '3x3', '3x4', '4x4', '4x6', '4x8', '6x8', '8x8'];
const gridTotal: Record<GridSize, number> = { '2x2': 4, '2x3': 6, '3x3': 9, '3x4': 12, '4x4': 16, '4x6': 24, '4x8': 32, '6x8': 48, '8x8': 64 };
const gridCols: Record<GridSize, number> = { '2x2': 2, '2x3': 3, '3x3': 3, '3x4': 4, '4x4': 4, '4x6': 6, '4x8': 8, '6x8': 8, '8x8': 8 };

interface GridCamera {
  cameraId: string;
  cameraName: string;
  gridPosition: number;
}

type Mode = 'normal' | 'create' | 'edit';

export default function SiteLiveWallPage() {
  const navigate = useNavigate();
  // `?view=` — the layout to open on, set by the Recorded page's toggle on its way back.
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('live-wall.edit');
  const canEditCamera = hasPermission('cameras.edit');
  const canConfigCamera = hasPermission('cameras.config');
  const canViewRecordings = hasPermission('recordings.view');

  const { selectedNode, selectedNodeId } = useNode();
  const nodeId = selectedNodeId || '';
  const nodeName = selectedNode?.name || 'this site';

  const { streams, states, subscribe, unsubscribe, manager } = useNodeStream();

  const [mode, setMode] = useState<Mode>('normal');
  const [savedViews, setSavedViews] = useState<SiteLiveWallView[]>([]);
  const [currentView, setCurrentView] = useState<SiteLiveWallView | null>(null);
  const [loadingLayout, setLoadingLayout] = useState(true);

  // Sparse array indexed by position — null is an empty slot, which is a real state.
  const [gridCameras, setGridCameras] = useState<(GridCamera | null)[]>([]);
  const [gridSize, setGridSize] = useState<GridSize>('2x2');
  // Cells combined into one bigger window — see the shared wallLayout. Empty = a plain grid.
  const [merges, setMerges] = useState<WallMerge[]>([]);
  const [layoutName, setLayoutName] = useState('');

  // Picker catalogue for this node.
  const [cameras, setCameras] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [loadingCameras, setLoadingCameras] = useState(false);

  const [pickerSlot, setPickerSlot] = useState<number | null>(null);

  // Camera edit — the wall edits a camera IN PLACE, in the same modal the camera pages use,
  // instead of navigating to its detail page: a one-field change shouldn't cost the operator
  // the wall (and every tile's live track). A slot carries only id + name, so the full row is
  // looked up on click, and null means the modal is closed.
  const [editCamera, setEditCamera] = useState<any | null>(null);
  const [editCameraLoading, setEditCameraLoading] = useState(false);
  const [layoutDropdownOpen, setLayoutDropdownOpen] = useState(false);
  const [gridSizeDropdownOpen, setGridSizeDropdownOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);
  const [showPing, setShowPing] = useState(false);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenCameraId, setFullscreenCameraId] = useState<string | null>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const tileWrapperRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const preCreateViewRef = useRef<SiteLiveWallView | null>(null);

  // Cameras this page currently has subscribed, for diffing.
  const subscribedRef = useRef<Set<string>>(new Set());

  const showToast = useCallback((message: string, type: 'success' | 'error' | 'warning' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // ─── Reconcile subscriptions to the grid (layout-diff) ──────
  // Unsubscribe only what left, subscribe only what arrived, so switching to a layout that
  // reuses cameras does NOT rebuild those tracks.
  useEffect(() => {
    if (!nodeId) return;
    const desired = new Set<string>();
    for (const cam of gridCameras) if (cam) desired.add(cam.cameraId);

    for (const cameraId of subscribedRef.current) {
      if (!desired.has(cameraId)) unsubscribe(nodeId, cameraId);
    }
    for (const cam of gridCameras) {
      if (cam && !subscribedRef.current.has(cam.cameraId)) {
        subscribe(nodeId, nodeName, cam.cameraId, cam.cameraName);
      }
    }
    subscribedRef.current = desired;
  }, [gridCameras, nodeId, nodeName, subscribe, unsubscribe]);

  // Release this page's subscriptions on unmount. The shared connection stays warm.
  useEffect(() => {
    const held = subscribedRef;
    return () => {
      for (const cameraId of held.current) unsubscribe(nodeId, cameraId);
      held.current = new Set();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId, unsubscribe]);

  // ─── Load a layout onto the wall ────────────────────────────
  const loadViewIntoGrid = useCallback((view: SiteLiveWallView) => {
    const size = (view.grid_size as GridSize) || '2x2';
    const total = gridTotal[size] ?? 4;
    const grid: (GridCamera | null)[] = Array(total).fill(null);
    for (const cam of view.cameras || []) {
      if (cam.gridPosition >= 0 && cam.gridPosition < total) grid[cam.gridPosition] = cam;
    }
    setGridCameras(grid);
    setGridSize(size);
    setMerges(normalizeWallMerges(view.merges, size));
    setCurrentView(view);
    setFullscreenCameraId(null);
    setMode('normal');
  }, []);

  // ─── Saved layouts for the SELECTED node ────────────────────
  // Re-runs when the site changes: layouts belong to a node, so switching sites in the title
  // bar replaces the whole wall rather than carrying one site's grid onto another's cameras.
  useEffect(() => {
    if (!nodeId) { setLoadingLayout(false); return; }
    let cancelled = false;
    setLoadingLayout(true);
    setSavedViews([]);
    setCurrentView(null);
    setGridCameras([]);
    setMode('normal');

    (async () => {
      try {
        const [views, defaultView] = await Promise.all([
          getNodeLiveWallViews(nodeId),
          getNodeLiveWallDefault(nodeId),
        ]);
        if (cancelled) return;
        setSavedViews(views);
        // Coming back from Recorded: the layout it was opened from, not the default. Consumed
        // once — left in the URL it would pin every later reload to that layout.
        const wanted = searchParams.get('view');
        const back = wanted ? views.find((v) => String(v.id) === wanted) : null;
        if (back) loadViewIntoGrid(back);
        else if (defaultView) loadViewIntoGrid(defaultView);
        if (wanted) setSearchParams({}, { replace: true });
      } catch (e: any) {
        if (!cancelled) {
          const body = e?.response?.data;
          showToast(body?.message || 'Failed to load this site’s layouts', 'error');
        }
      } finally {
        if (!cancelled) setLoadingLayout(false);
      }
    })();

    return () => { cancelled = true; };
    // The URL param is read once per site load, then cleared — not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId, loadViewIntoGrid, showToast]);

  /** Live → Recorded, carrying this wall's first six cameras and the layout to come back to. */
  const openRecorded = useCallback(() => {
    navigate(wallPlaybackHref('/live-wall/playback', liveWallHandOver(gridCameras, gridSize, merges), currentView?.id));
  }, [navigate, gridCameras, gridSize, merges, currentView]);

  // ─── Picker catalogue — only for someone who can edit ───────
  const canViewGroups = hasPermission('groups.view');
  useEffect(() => {
    // Also for someone who can only edit CAMERAS: this list is where the edit form's row comes
    // from, and the groups feed its group-assignment panel.
    if (!nodeId || (!canEdit && !canEditCamera)) return;
    let cancelled = false;
    setLoadingCameras(true);
    (async () => {
      try {
        const [cams, grps] = await Promise.all([
          getNodeCameras(nodeId),
          canViewGroups ? getNodeGroups(nodeId) : getNodeGroupsForFilter(nodeId),
        ]);
        if (cancelled) return;
        // The node's /cameras answer arrives as an array or as `{ data, pagination }`
        // depending on the route — the same normalisation every other node adapter does.
        setCameras(Array.isArray(cams) ? cams : ((cams as any)?.data || (cams as any)?.cameras || []));
        setGroups(Array.isArray(grps) ? grps : []);
      } catch {
        if (!cancelled) setCameras([]);
      } finally {
        if (!cancelled) setLoadingCameras(false);
      }
    })();
    return () => { cancelled = true; };
  }, [nodeId, canEdit, canEditCamera, canViewGroups]);

  // The picker rebuilds the group tree itself, so hand it a flat list either way.
  const flatGroups = useMemo(() => {
    const out: any[] = [];
    const walk = (nodes: any[]) => {
      for (const n of nodes || []) {
        out.push(n);
        if (n.children) walk(n.children);
      }
    };
    walk(groups);
    return out;
  }, [groups]);

  // ─── Browser fullscreen (with iOS Safari's webkit prefix) ───
  const getFullscreenElement = useCallback(
    () => document.fullscreenElement || (document as any).webkitFullscreenElement || null,
    [],
  );

  useEffect(() => {
    const onFsChange = () => {
      const el = getFullscreenElement() as HTMLElement | null;
      setIsFullscreen(!!el);
      setFullscreenCameraId(el?.getAttribute?.('data-cameraid') || null);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, [getFullscreenElement]);

  const requestFullscreenOn = useCallback((el: HTMLElement | null | undefined) => {
    const req = el && (el.requestFullscreen?.bind(el) || (el as any).webkitRequestFullscreen?.bind(el));
    if (!req) return;
    req()
      .then(() => { try { (screen.orientation as any)?.lock?.('landscape').catch(() => {}); } catch {} })
      .catch(() => {});
  }, []);

  const exitFullscreen = useCallback(() => {
    const exit = document.exitFullscreen?.bind(document) || (document as any).webkitExitFullscreen?.bind(document);
    exit?.()
      .then(() => { try { (screen.orientation as any)?.unlock?.(); } catch {} })
      .catch(() => {});
  }, []);

  const toggleWallFullscreen = useCallback(() => {
    if (getFullscreenElement()) exitFullscreen();
    else requestFullscreenOn(gridContainerRef.current);
  }, [getFullscreenElement, exitFullscreen, requestFullscreenOn]);

  /**
   * Expand ONE tile, via the Fullscreen API on the tile's own wrapper rather than a CSS
   * overlay: the same <video> stays mounted, so the stream keeps playing instead of going
   * black. Quality is bumped while expanded and dropped on the way out.
   */
  const toggleTileFullscreen = useCallback((cameraId: string) => {
    if (fullscreenCameraId === cameraId) {
      exitFullscreen();
      manager()?.changeQuality(nodeId, cameraId, 'low');
    } else {
      requestFullscreenOn(tileWrapperRefs.current.get(cameraId));
      manager()?.changeQuality(nodeId, cameraId, 'high');
    }
  }, [fullscreenCameraId, exitFullscreen, requestFullscreenOn, manager, nodeId]);

  // ─── Editing a tile's CAMERA (not the layout) ───────────────
  /**
   * Open the camera edit modal for a tile.
   *
   * Leaves fullscreen first: the modal renders outside the element handed to the Fullscreen
   * API, so on a fullscreen wall — or an expanded tile — it would otherwise open on a surface
   * the browser isn't showing.
   */
  const handleEditCamera = useCallback(async (cameraId: string) => {
    if (getFullscreenElement()) exitFullscreen();
    setEditCameraLoading(true);
    try {
      // Re-read the node's list rather than trusting the catalogue fetched on mount: the form
      // writes every field back, so it must start from what the node holds now. The cached
      // entry is the fallback for a tunnel that has since gone quiet.
      let cam: any = null;
      try {
        const data: any = await getNodeCameras(nodeId);
        const list = Array.isArray(data) ? data : (data?.data || data?.cameras || []);
        cam = list.find((c: any) => (c.id || c.camera_id) === cameraId) || null;
      } catch {
        cam = null;
      }
      if (!cam) cam = cameras.find((c: any) => (c.id || c.camera_id) === cameraId) || null;
      if (!cam) throw new Error('camera not found');
      setEditCamera(cam);
    } catch {
      showToast('Failed to load camera details', 'error');
    } finally {
      setEditCameraLoading(false);
    }
  }, [nodeId, cameras, getFullscreenElement, exitFullscreen, showToast]);

  /**
   * After a save. The tile's label comes from the LAYOUT's stored slot, not the camera list,
   * so a rename is patched in place — on the wall, in the loaded layout and in the saved-layout
   * menu — rather than reloading the layout, which would re-signal every tile.
   */
  const handleCameraSaved = useCallback(async () => {
    const id = editCamera?.id || editCamera?.camera_id;
    if (!id) return;
    try {
      const data: any = await getNodeCameras(nodeId);
      const list = Array.isArray(data) ? data : (data?.data || data?.cameras || []);
      setCameras(list);
      const name = list.find((c: any) => (c.id || c.camera_id) === id)?.name;
      if (!name) return;
      const rename = (arr: any[]) =>
        (arr || []).map((c: any) => (c && c.cameraId === id ? { ...c, cameraName: name } : c));
      setGridCameras((prev) => rename(prev));
      setCurrentView((prev) => (prev ? { ...prev, cameras: rename(prev.cameras) } : prev));
      setSavedViews((prev) => prev.map((v) => ({ ...v, cameras: rename(v.cameras) })));
    } catch {
      /* The name refresh is cosmetic — the save itself already succeeded. */
    }
  }, [editCamera, nodeId]);

  // ─── Mobile portrait ───────────────────────────────────────
  const [isMobilePortrait, setIsMobilePortrait] = useState(false);
  useEffect(() => {
    const check = () => setIsMobilePortrait(window.innerWidth < 768 && window.innerHeight > window.innerWidth);
    check();
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, []);

  // ─── Latency ───────────────────────────────────────────────
  // One site, one peer connection, so one figure for the whole wall.
  const pingData = usePeerLatency(
    useCallback(() => {
      const mgr = manager();
      if (!mgr || !nodeId) return [];
      return [{ key: nodeId, pc: mgr.getNodePeerConnection(nodeId) }];
    }, [manager, nodeId]),
    showPing,
  );
  const siteLatency = pingData.get(nodeId) || null;

  // ─── Editing ────────────────────────────────────────────────
  const isEditing = mode === 'create' || mode === 'edit';

  const usedCameraIds = useMemo(() => {
    const ids = new Set<string>();
    for (const cam of gridCameras) if (cam) ids.add(cam.cameraId);
    return ids;
  }, [gridCameras]);

  const handleCameraSelected = useCallback((cameraId: string, cameraName: string) => {
    if (pickerSlot === null) return;
    setGridCameras((prev) => {
      const next = [...prev];
      next[pickerSlot] = { cameraId, cameraName, gridPosition: pickerSlot };
      return next;
    });
    setPickerSlot(null);
  }, [pickerSlot]);

  const handleRemoveFromSlot = useCallback((position: number) => {
    setGridCameras((prev) => {
      const next = [...prev];
      next[position] = null;
      return next;
    });
  }, []);

  const handleGridSizeChange = useCallback((size: GridSize) => {
    setGridSize(size);
    // Merges are rectangles on ONE grid shape; on another they would cover different cells,
    // so a new size starts plain. Said out loud, because it undoes work.
    if (merges.length) showToast('Combined windows were reset for the new grid size', 'warning');
    setMerges([]);
    setGridCameras((prev) => {
      const total = gridTotal[size];
      const next = prev.slice(0, total);
      while (next.length < total) next.push(null);
      return next;
    });
  }, [merges.length, showToast]);

  /** A ready-made shape: size + merges at once, cameras flowed into its windows in order. */
  const handlePreset = useCallback((preset: WallPreset) => {
    const size = preset.gridSize as GridSize;
    setGridSize(size);
    setMerges(preset.merges);
    setGridCameras((prev) => flowWallCameras(prev.filter(Boolean) as GridCamera[], size, preset.merges));
  }, []);

  const enterCreateMode = useCallback(() => {
    preCreateViewRef.current = currentView;
    setGridCameras(Array(gridTotal['2x2']).fill(null));
    setGridSize('2x2');
    setMerges([]);
    setLayoutName('');
    setCurrentView(null);
    setFullscreenCameraId(null);
    setMode('create');
  }, [currentView]);

  const handleEditView = useCallback((view: SiteLiveWallView) => {
    loadViewIntoGrid(view);
    setLayoutName(view.name);
    setLayoutDropdownOpen(false);
    setMode('edit');
  }, [loadViewIntoGrid]);

  const cancelMode = useCallback(() => {
    if (mode === 'create') {
      const prev = preCreateViewRef.current;
      if (prev) { loadViewIntoGrid(prev); return; }
      setMerges([]);
      setGridCameras([]);
    } else if (mode === 'edit' && currentView) {
      loadViewIntoGrid(currentView);
      return;
    }
    setMode('normal');
  }, [mode, currentView, loadViewIntoGrid]);

  const handleSave = useCallback(async () => {
    if (!layoutName.trim()) { showToast('Enter a layout name', 'warning'); return; }
    if (!nodeId) return;

    const camerasForSave = gridCameras
      .map((cam, i) => (cam ? { ...cam, gridPosition: i } : null))
      .filter(Boolean) as GridCamera[];

    try {
      if (mode === 'create') {
        const view = await createNodeLiveWallView(nodeId, {
          name: layoutName.trim(), cameras: camerasForSave, gridSize, merges,
        });
        setSavedViews((prev) => [view, ...prev]);
        setCurrentView(view);
        showToast(`Layout "${view.name}" saved`);
      } else if (mode === 'edit' && currentView) {
        const view = await updateNodeLiveWallView(nodeId, currentView.id, {
          name: layoutName.trim(), cameras: camerasForSave, gridSize, merges,
        });
        setSavedViews((prev) => prev.map((v) => (v.id === view.id ? view : v)));
        setCurrentView(view);
        showToast('Layout updated');
      }
      setMode('normal');
    } catch (e: any) {
      showToast(e?.response?.data?.message || 'Failed to save layout', 'error');
    }
  }, [layoutName, gridCameras, gridSize, merges, mode, currentView, nodeId, showToast]);

  const handleDelete = useCallback(async (view: SiteLiveWallView) => {
    if (!nodeId) return;
    try {
      await deleteNodeLiveWallView(nodeId, view.id);
      setSavedViews((prev) => prev.filter((v) => v.id !== view.id));
      if (currentView?.id === view.id) {
        setCurrentView(null);
        setGridCameras([]);
        setMerges([]);
        setMode('normal');
      }
      showToast('Layout deleted');
    } catch (e: any) {
      showToast(e?.response?.data?.message || 'Failed to delete layout', 'error');
    }
  }, [currentView, nodeId, showToast]);

  const handleToggleDefault = useCallback(async (view: SiteLiveWallView) => {
    if (!nodeId) return;
    try {
      if (view.is_default) {
        const updated = await clearNodeLiveWallDefault(nodeId, view.id);
        setSavedViews((prev) => prev.map((v) => (v.id === view.id ? updated : v)));
        if (currentView?.id === view.id) setCurrentView(updated);
        showToast('Default cleared');
      } else {
        const updated = await setNodeLiveWallDefault(nodeId, view.id);
        // Exactly one default per node, so the others lose the star locally too.
        setSavedViews((prev) =>
          prev.map((v) => (v.id === view.id ? updated : v.is_default ? { ...v, is_default: false } : v)),
        );
        if (currentView?.id === view.id) setCurrentView(updated);
        showToast('Set as the layout this site opens on');
      }
    } catch (e: any) {
      showToast(e?.response?.data?.message || 'Failed to update default', 'error');
    }
  }, [currentView, nodeId, showToast]);

  // ─── Derived ────────────────────────────────────────────────
  const cols = gridCols[gridSize];
  const total = gridTotal[gridSize];
  const activeCameras = gridCameras.filter(Boolean) as GridCamera[];

  // Drag-to-combine. A plain click on an EMPTY window still opens the picker, exactly as the
  // bare slot's onClick did; a click on a camera tile does nothing, as before.
  const wall = useWallMerge<GridCamera>({
    gridSize,
    merges,
    setMerges,
    gridCameras,
    setGridCameras,
    isEditing,
    onWindowClick: useCallback((w: { position: number }) => {
      if (!gridCameras[w.position]) setPickerSlot(w.position);
    }, [gridCameras]),
  });
  // Column widths / row heights, dragged on the lines between windows — per saved layout, in
  // this browser only (the saved layout itself has no field for them). A layout being created
  // has no id yet, so its sizes last until it is saved.
  const rows = Math.ceil(total / cols);
  const tracks = useWallTrackSizes(currentView && nodeId ? `trinetra.liveWall.sizes.${nodeId}.${currentView.id}` : null, rows, cols);
  const windowCount = wall.windows.length;

  // ─── No site picked ─────────────────────────────────────────
  // Reachable: the sidebar's site scope renders with no node selected until one is chosen.
  if (!nodeId) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[60vh] text-muted-foreground">
        <Monitor className="h-12 w-12 mb-3 opacity-30" />
        <p className="text-sm">Pick a site to see its Live Wall</p>
        <p className="text-xs mt-1">Layouts belong to a site, so this page follows the one in the title bar.</p>
      </div>
    );
  }

  if (loadingLayout) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[60vh] gap-4">
        <div className="relative">
          <div className="animate-spin rounded-full h-12 w-12 border-[3px] border-muted border-t-brand" />
          <Monitor className="absolute inset-0 m-auto h-5 w-5 text-brand" />
        </div>
        <div className="text-center">
          <p className="text-sm font-medium text-foreground">Loading Live Wall</p>
          <p className="text-xs text-muted-foreground mt-1">Fetching {nodeName}’s layouts…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* ─── Toolbar ─────────────────────────────────────────── */}
      <div className="mb-2 flex flex-shrink-0 items-center gap-2 rounded-lg border border-border bg-card px-2.5 py-1.5 sm:px-3 sm:py-2">
        {/* ── Left: which wall, and what is on it ── */}
        {isEditing ? (
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-2.5">
            <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-brand/10 text-brand sm:h-9 sm:w-9">
              <Monitor className="h-4 w-4 sm:h-5 sm:w-5" />
            </span>
            <div className="flex min-w-0 flex-col justify-center leading-tight">
              <input
                type="text"
                value={layoutName}
                onChange={(e) => setLayoutName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                placeholder="Layout name…"
                autoFocus
                className="w-full max-w-xs rounded border border-border bg-background px-2 py-0.5 text-[15px] font-bold text-foreground placeholder:font-normal placeholder:text-muted-foreground focus:border-brand focus:outline-none sm:text-base"
              />
              <span className="mt-0.5 hidden max-w-full truncate whitespace-nowrap text-[11px] text-muted-foreground sm:block">
                <span className="font-semibold uppercase tracking-wide text-brand">{mode === 'create' ? 'New' : 'Editing'}</span>
                {` · on ${nodeName} · click a window to add a camera · drag across windows to combine them`}
              </span>
            </div>
          </div>
        ) : (
          <div className="flex min-w-0 flex-1 items-center">
            <ToolbarIdentity
              icon={Monitor}
              kind="Live Wall:"
              title={currentView?.name || 'no layout selected'}
              titleText={currentView ? `Live Wall: ${currentView.name}` : undefined}
              detail={
                currentView
                  ? `${nodeName} · ${activeCameras.length} camera${activeCameras.length === 1 ? '' : 's'} · ${gridSize} grid${merges.length ? ` · ${windowCount} windows` : ''}`
                  : `${nodeName} · pick a saved layout, or create one`
              }
            />
          </div>
        )}

        {/* ── Centre: which layout, or mid-edit its shape ── */}
        <div className="flex flex-none items-center justify-center">
          {isEditing ? (
            <div className="relative">
              <button
                onClick={() => setGridSizeDropdownOpen((o) => !o)}
                className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium bg-muted text-foreground hover:bg-accent transition-colors"
              >
                <LayoutGrid className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span>{gridSize}</span>
                <ChevronDown className={`h-3 w-3 sm:h-3.5 sm:w-3.5 transition-transform ${gridSizeDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {gridSizeDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setGridSizeDropdownOpen(false)} />
                  <div className="absolute top-full right-0 mt-1 w-64 bg-card border border-border rounded-lg shadow-xl z-50 overflow-hidden">
                    <WallGridMenu
                      gridSize={gridSize}
                      merges={merges}
                      sizes={GRID_SIZES.map((size) => ({ size, total: gridTotal[size] }))}
                      onSize={(size) => { handleGridSizeChange(size as GridSize); setGridSizeDropdownOpen(false); }}
                      onPreset={(preset) => { handlePreset(preset); setGridSizeDropdownOpen(false); }}
                    />
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-1.5 sm:gap-2">
            <WallModeToggle mode="live" canRecorded={canViewRecordings} onChange={openRecorded} />
            <div className="relative">
              <button
                onClick={() => setLayoutDropdownOpen((o) => !o)}
                className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium bg-muted text-foreground hover:bg-accent transition-colors"
              >
                <LayoutGrid className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Layouts</span>
                <ChevronDown className={`h-3 w-3 sm:h-3.5 sm:w-3.5 transition-transform ${layoutDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {layoutDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setLayoutDropdownOpen(false)} />
                  <div className="absolute top-full right-0 mt-1 w-72 bg-card border border-border rounded-lg shadow-xl z-50 overflow-hidden">
                    {savedViews.length === 0 ? (
                      <p className="px-3 py-4 text-sm text-muted-foreground text-center">No saved layouts on {nodeName}</p>
                    ) : (
                      <div className="max-h-60 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
                        {savedViews.map((view) => (
                          <div
                            key={view.id}
                            className={`flex items-center gap-2 px-3 py-2 hover:bg-accent/50 transition-colors ${
                              currentView?.id === view.id ? 'bg-brand/10' : ''
                            }`}
                          >
                            <button
                              onClick={() => { loadViewIntoGrid(view); setLayoutDropdownOpen(false); }}
                              className="group/name flex-1 text-left min-w-0"
                            >
                              <div className="flex items-center gap-1.5">
                                <p title={view.name} className="text-sm font-medium text-foreground truncate transition-colors group-hover/name:text-brand">
                                  {view.name}
                                </p>
                                {view.is_default && <Star className="h-3 w-3 text-amber-500 fill-amber-500 flex-shrink-0" />}
                              </div>
                              <p className="text-[10px] text-muted-foreground">
                                {(view.cameras || []).length} cameras · {view.grid_size}
                              </p>
                            </button>

                            {canEdit && (
                              <>
                                <Tooltip label="Edit layout" side="top" className="flex-shrink-0">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleEditView(view); }}
                                    className="p-1 text-muted-foreground hover:text-brand transition-colors"
                                    aria-label="Edit layout"
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                </Tooltip>
                                <Tooltip label={view.is_default ? 'Remove default' : 'Set as default'} side="top" className="flex-shrink-0">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleToggleDefault(view); }}
                                    className="p-1 text-muted-foreground hover:text-amber-500 transition-colors"
                                    aria-label={view.is_default ? 'Remove default' : 'Set as default'}
                                  >
                                    {view.is_default
                                      ? <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />
                                      : <StarOff className="h-3.5 w-3.5" />}
                                  </button>
                                </Tooltip>
                                <Tooltip label="Delete layout" side="top" className="flex-shrink-0">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleDelete(view); }}
                                    className="p-1 text-muted-foreground hover:text-red-400 transition-colors"
                                    aria-label="Delete layout"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </Tooltip>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            </div>
          )}
        </div>

        {/* ── Right: the actions ── */}
        <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:gap-2">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                className="flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium bg-brand text-white hover:bg-brand/90 transition-colors"
              >
                <Save className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Save</span>
              </button>
              <button
                onClick={cancelMode}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                title="Cancel"
                aria-label="Cancel"
              >
                <X className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              {canEdit && (
                <Tooltip label="New layout" side="bottom">
                  <button
                    onClick={enterCreateMode}
                    aria-label="New layout"
                    className="p-1.5 rounded-lg bg-brand text-white shadow-sm ring-1 ring-inset ring-white/10 hover:bg-brand/90 hover:shadow active:scale-[0.95] transition-all"
                  >
                    <Plus className="h-4 w-4" strokeWidth={2.5} />
                  </button>
                </Tooltip>
              )}
              {/* Only once a line between windows has been dragged — the way back to even. */}
              {tracks.uneven && (
                <Tooltip label="Even out window sizes (or double-click a line between windows)" side="bottom">
                  <button
                    onClick={tracks.reset}
                    aria-label="Even out window sizes"
                    className="flex items-center gap-1 p-1.5 rounded-lg text-xs sm:text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                  >
                    <LayoutGrid className="h-4 w-4 sm:h-5 sm:w-5" />
                    <span className="hidden lg:inline">Even sizes</span>
                  </button>
                </Tooltip>
              )}
              <Tooltip label={showPing ? 'Hide network latency' : 'Show network latency'} side="bottom">
                <button
                  onClick={() => setShowPing((s) => !s)}
                  aria-label="Toggle network latency"
                  className={`p-1.5 rounded-lg transition-colors ${
                    showPing ? 'bg-green-500/15 text-green-400' : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                  }`}
                >
                  <Activity className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </Tooltip>
              <Tooltip label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} side="bottom">
                <button
                  onClick={toggleWallFullscreen}
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                  aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                >
                  {isFullscreen ? <Minimize2 className="h-4 w-4 sm:h-5 sm:w-5" /> : <Maximize2 className="h-4 w-4 sm:h-5 sm:w-5" />}
                </button>
              </Tooltip>
            </>
          )}
        </div>
      </div>

      {/* How to shape the wall — only while editing, folded to one line once read. */}
      {isEditing && <WallEditGuide />}

      {/* ─── The wall ─────────────────────────────────────────── */}
      <div
        ref={gridContainerRef}
        className={`flex-1 min-h-0 overflow-hidden relative ${isFullscreen ? 'bg-black' : ''}`}
        style={
          isFullscreen
            ? isMobilePortrait
              ? {
                  width: '100vh',
                  height: '100vw',
                  transform: 'rotate(90deg)',
                  transformOrigin: 'top left',
                  position: 'absolute',
                  top: 0,
                  left: '100vw',
                  padding: '2px',
                }
              : { width: '100vw', height: '100vh', padding: '2px' }
            : undefined
        }
      >
        {isFullscreen && !fullscreenCameraId && (
          <button
            onClick={toggleWallFullscreen}
            className="absolute top-3 right-3 z-20 rounded-lg p-2 text-white bg-black/50 hover:bg-black/70 transition-colors"
            aria-label="Exit fullscreen"
          >
            <Minimize2 className="h-5 w-5" />
          </button>
        )}

        {!currentView && !isEditing ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <Monitor className="h-12 w-12 mb-3 opacity-30" />
            <p className="text-sm mb-4">
              {savedViews.length === 0 ? `No layouts on ${nodeName} yet` : 'No layout loaded'}
            </p>
            {canEdit && (
              <button
                onClick={enterCreateMode}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-brand text-white hover:bg-brand/90 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Create New Layout
              </button>
            )}
          </div>
        ) : (
          <div
            className={`relative grid h-full ${isFullscreen ? 'gap-0.5' : 'gap-1.5 sm:gap-2'} ${wall.gridProps.className}`}
            style={{
              gridTemplateColumns: trackTemplate(tracks.colSizes),
              gridTemplateRows: trackTemplate(tracks.rowSizes),
              ...wall.gridProps.style,
            }}
            onPointerDown={wall.gridProps.onPointerDown}
            onPointerMove={wall.gridProps.onPointerMove}
            onPointerUp={wall.gridProps.onPointerUp}
            onPointerCancel={wall.gridProps.onPointerCancel}
          >
            <WallGridResizer
              rows={rows}
              cols={cols}
              windows={wall.windows}
              colSizes={tracks.colSizes}
              rowSizes={tracks.rowSizes}
              onChange={tracks.onChange}
            />
            {/* One item per WINDOW, not per cell: a merged window is one item spanning its
                rectangle, and the cells it covers are simply not drawn. */}
            {wall.windows.map((w) => {
              const i = w.position;
              const cam = gridCameras[i] || null;

              if (!cam) {
                return (
                  <WallEmptyWindow
                    key={`slot-${i}`}
                    window={w}
                    isEditing={isEditing}
                    onSplit={() => wall.split(i)}
                  />
                );
              }

              const key = `${nodeId}:${cam.cameraId}`;
              const isTileFullscreen = fullscreenCameraId === cam.cameraId;
              const showActions = !isEditing;
              // Keyed by camera, NOT by slot: a camera that moves position (or survives a
              // layout switch) keeps its tile — and therefore its <video> and live track.
              return (
                <div
                  key={cam.cameraId}
                  data-cameraid={cam.cameraId}
                  ref={(el: HTMLDivElement | null) => {
                    const m = tileWrapperRefs.current;
                    if (el) m.set(cam.cameraId, el); else m.delete(cam.cameraId);
                  }}
                  data-wallpos={i}
                  style={wallGridArea(w)}
                  className={`relative overflow-hidden rounded-lg ${isTileFullscreen ? 'bg-black' : ''}`}
                >
                  <StreamTile
                    nodeId={nodeId}
                    nodeName={nodeName}
                    cameraId={cam.cameraId}
                    cameraName={cam.cameraName}
                    stream={streams.get(key) || null}
                    state={states.get(key) || 'pending'}
                    isFullscreen={isTileFullscreen}
                    fillContainer
                    pingInfo={showPing ? siteLatency : null}
                    onDoubleClick={() => toggleTileFullscreen(cam.cameraId)}
                    onRetry={() => manager()?.retryCamera(nodeId, cam.cameraId)}
                    onStalled={() => manager()?.handleCameraStall(nodeId, cam.cameraId)}
                    onPlaying={() => manager()?.handleCameraPlaying(nodeId, cam.cameraId)}
                    // Every destination is already node-scoped to the site in context, so
                    // unlike the all-sites wall there is no node to switch first.
                    onEdit={showActions && canEditCamera
                      ? () => handleEditCamera(cam.cameraId)
                      : undefined}
                    onConfig={showActions && canConfigCamera
                      ? () => navigate(`/nodes/${nodeId}/cameras/${cam.cameraId}/config`)
                      : undefined}
                    onPlayback={showActions && canViewRecordings
                      ? () => navigate(`/recordings?camera=${cam.cameraId}`)
                      : undefined}
                  />

                  {isTileFullscreen && (
                    <button
                      onClick={() => toggleTileFullscreen(cam.cameraId)}
                      className="absolute top-4 right-4 z-[60] p-2 bg-black/60 hover:bg-black/80 text-white rounded-lg transition-colors"
                      title="Exit fullscreen"
                      aria-label="Exit fullscreen"
                    >
                      <Minimize2 className="h-5 w-5" />
                    </button>
                  )}

                  {isEditing && !isTileFullscreen && (
                    <button
                      onClick={() => handleRemoveFromSlot(i)}
                      className="absolute top-1.5 right-1.5 z-10 p-1 bg-black/60 hover:bg-red-500/80 text-white rounded-md transition-colors"
                      title="Remove"
                      aria-label="Remove camera from slot"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}

                  {isEditing && !isTileFullscreen && w.merged && <WallSplitButton onSplit={() => wall.split(i)} />}
                </div>
              );
            })}

            <WallSelectionOverlay selection={wall.selection} dragging={wall.dragging} />
          </div>
        )}

        {/* What the selection will become, and Merge / Cancel. */}
        {isEditing && <WallMergeBar pending={wall.pending} onMerge={wall.commit} onCancel={wall.cancel} />}
      </div>

      {/* ─── Camera picker — the shared one, same as the local dashboard's ─── */}
      <LiveWallCameraPicker
        open={pickerSlot !== null}
        onClose={() => setPickerSlot(null)}
        onSelect={handleCameraSelected}
        cameras={cameras}
        groups={flatGroups}
        loading={loadingCameras}
        usedCameraIds={usedCameraIds}
      />

      {/* ─── Camera edit — in place, so the wall keeps streaming ── */}
      <CameraFormModal
        isOpen={!!editCamera}
        onClose={() => setEditCamera(null)}
        onSuccess={handleCameraSaved}
        camera={editCamera}
        nodeId={nodeId}
        groups={flatGroups}
      />

      {/* Covers the tunnel round trip that fetches the row the form needs, so a click on a
          slow link isn't a dead button. */}
      {editCameraLoading && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
          <div className="px-4 py-2.5 rounded-lg bg-card text-sm text-muted-foreground shadow-lg flex items-center gap-2">
            <div className="animate-spin rounded-full h-3.5 w-3.5 border-t-2 border-brand" />
            Loading camera…
          </div>
        </div>
      )}

      {/* ─── Toast ────────────────────────────────────────────── */}
      {toast && (
        <div
          className={`fixed bottom-4 right-4 z-[60] px-4 py-2.5 rounded-lg text-sm font-medium shadow-lg transition-all ${
            toast.type === 'success'
              ? 'bg-emerald-500/90 text-white'
              : toast.type === 'warning'
                ? 'bg-amber-500/90 text-white'
                : 'bg-red-500/90 text-white'
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
